import React, { useEffect, useMemo, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Typography,
} from '@mui/material';
import { IInvoice, IInvoiceReceipt } from '../../types';
import { getInvoiceReceipts, linkReceipts } from '../../services';
import { PLATFORM_LABEL, formatAmount, formatDate } from '../../uiHelpers';

interface Props {
	invoice: IInvoice;
	open: boolean;
	onClose: () => void;
	onLinked: (invoice: IInvoice) => void;
}

const MAIN_CURRENCY = 'USD';

const normalizeCurrency = (currency: string): string => currency.trim().toUpperCase();

/**
 * Calcula cuánto cubre un recibo expresado en la moneda de la factura.
 *
 * Esta lógica debe mantenerse alineada con backend:
 *
 * - Si el recibo y la factura tienen la misma moneda:
 *   usa transactionAmount.
 *
 * - Si la factura está en USD:
 *   usa amountInMainCurrency.
 *
 * - Si la factura está en moneda local:
 *   usa transactionAmountInLocalCurrency.
 *
 * - Si no existe transactionAmountInLocalCurrency:
 *   usa amountInMainCurrency * rateToConvertToLocalCurrency.
 *
 * Importante:
 * Esto es solo una proyección visual. La validación real y el recálculo final
 * se hacen en backend al vincular los recibos.
 */
const coverageOf = (receipt: IInvoiceReceipt, invoiceCurrency: string): number => {
	const receiptCurrency = normalizeCurrency(receipt.currency);
	const cleanInvoiceCurrency = normalizeCurrency(invoiceCurrency);

	if (receiptCurrency === cleanInvoiceCurrency) {
		return receipt.transactionAmount;
	}

	if (cleanInvoiceCurrency === MAIN_CURRENCY) {
		return receipt.amountInMainCurrency;
	}

	if (
		receipt.transactionAmountInLocalCurrency !== undefined &&
		receipt.transactionAmountInLocalCurrency !== null &&
		receipt.transactionAmountInLocalCurrency > 0
	) {
		return receipt.transactionAmountInLocalCurrency;
	}

	if (
		receipt.rateToConvertToLocalCurrency !== undefined &&
		receipt.rateToConvertToLocalCurrency !== null &&
		receipt.rateToConvertToLocalCurrency > 0
	) {
		return receipt.amountInMainCurrency * receipt.rateToConvertToLocalCurrency;
	}

	// Si no se puede convertir a la moneda de la factura, no proyectamos cobertura.
	// En backend esto también terminaría fallando al intentar vincular.
	return 0;
};

export const LinkReceiptsDialog = ({ invoice, open, onClose, onLinked }: Props): JSX.Element => {
	const [receipts, setReceipts] = useState<IInvoiceReceipt[]>([]);
	const [selected, setSelected] = useState<Set<string>>(new Set());
	const [loading, setLoading] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (!open) return;

		setSelected(new Set());
		setError(null);
		setLoading(true);

		/**
		 * Para vincular anticipos solo cargamos recibos:
		 * - de la misma saleOrder
		 * - sin invoiceId
		 *
		 * El recibo se asigna entero a una sola factura.
		 */
		getInvoiceReceipts({
			saleOrderId: invoice.saleOrderId,
			unlinked: true,
		})
			.then(setReceipts)
			.catch(() => setError('No se pudieron cargar los anticipos disponibles.'))
			.finally(() => setLoading(false));
	}, [open, invoice.saleOrderId]);

	const toggle = (id: string): void => {
		setSelected((prev) => {
			const next = new Set(prev);

			if (next.has(id)) {
				next.delete(id);
			} else {
				next.add(id);
			}

			return next;
		});
	};

	const projectedCoverage = useMemo(
		() =>
			receipts
				.filter((receipt) => selected.has(receipt.id))
				.reduce((acc, receipt) => acc + coverageOf(receipt, invoice.currency), 0),
		[receipts, selected, invoice.currency],
	);

	const projectedPending = useMemo(
		() => Math.max(invoice.pendingAmount - projectedCoverage, 0),
		[invoice.pendingAmount, projectedCoverage],
	);

	const handleConfirm = async (): Promise<void> => {
		if (selected.size === 0) return;

		setSubmitting(true);
		setError(null);

		try {
			const updated = await linkReceipts(invoice.id, Array.from(selected));
			onLinked(updated);
			onClose();
		} catch {
			setError('No se pudieron vincular los anticipos.');
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle>Vincular recibos · {invoice.invoiceNumber}</DialogTitle>

			<DialogContent>
				<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
					Saldo pendiente actual: {formatAmount(invoice.pendingAmount, invoice.currency)}
				</Typography>

				{loading ? (
					<Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
						<CircularProgress size={28} />
					</Box>
				) : receipts.length === 0 ? (
					<Alert severity="info">No hay anticipos libres en esta orden.</Alert>
				) : (
					<List dense>
						{receipts.map((receipt) => {
							const coverageAmount = coverageOf(receipt, invoice.currency);
							const isDifferentCurrency = normalizeCurrency(receipt.currency) !== normalizeCurrency(invoice.currency);

							return (
								<ListItem key={receipt.id} disablePadding>
									<ListItemButton onClick={() => toggle(receipt.id)}>
										<ListItemIcon sx={{ minWidth: 36 }}>
											<Checkbox edge="start" checked={selected.has(receipt.id)} tabIndex={-1} />
										</ListItemIcon>

										<ListItemText
											primary={`${formatAmount(receipt.transactionAmount, receipt.currency)} · ${
												PLATFORM_LABEL[receipt.platformType]
											}`}
											secondary={`${formatDate(receipt.receiptDate)}${
												receipt.authorizationCode ? ` · ${receipt.authorizationCode}` : ''
											}${isDifferentCurrency ? ` · Cubre ${formatAmount(coverageAmount, invoice.currency)}` : ''}`}
										/>
									</ListItemButton>
								</ListItem>
							);
						})}
					</List>
				)}

				{selected.size > 0 && (
					<Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
						Cobertura proyectada: {formatAmount(projectedCoverage, invoice.currency)} · Nuevo saldo:{' '}
						{formatAmount(projectedPending, invoice.currency)}
					</Typography>
				)}

				{error && (
					<Alert severity="error" sx={{ mt: 2 }}>
						{error}
					</Alert>
				)}
			</DialogContent>

			<DialogActions>
				<Button onClick={onClose} disabled={submitting}>
					Cancelar
				</Button>

				<Button onClick={handleConfirm} variant="contained" disabled={submitting || selected.size === 0}>
					Vincular {selected.size > 0 ? `(${selected.size})` : ''}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
